import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ActionEditIcon } from '../../assets/icons/action-edit.svg';
import { ReactComponent as QRIcon } from '../../assets/icons/action-qr.svg';
import { ReactComponent as ResetIcon } from '../../assets/icons/action-reset.svg';
// import { ReactComponent as AlertIcon } from '../../assets/icons/alert-icon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as ActionDeleteIcon } from '../../assets/icons/delete-table.svg';
import { ReactComponent as DodIcon } from '../../assets/icons/dot-circle.svg';
import { ReactComponent as ActionDeleteIconDark } from '../../assets/icons/iconsDark/action-delete-icon-dark.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import {
  AvailableDevice,
  RequestDeviceInterFace,
} from '../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchAllDevices,
  fetchListFilters,
  setDeviceFilterData,
  setSortData,
  updateDeviceFilters,
} from '../../redux/slice/DeviceManagementSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { RootState } from '../../redux/store';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import DisableSwitchWarning from '../../shared-components/disable-warning/DisableWarning';
import ExportButton from '../../shared-components/export-button/ExportButton';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotificaion';
import SwitchButton from '../../shared-components/switch-button-field/SwitchButton';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import StationSidePanel from '../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import deviceApi from '../../utils/api/device';

import DeviceQrCode from './components/DeviceQrCode';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}
/**
 * Device management
 *
 */
function DeviceManagement() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useContext(ThemeContext);

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const { devices, page, limit, totalPages, totalResults, loading, filters, filterListLoading } =
    useAppSelector((states) => states.device);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const { sortBy, sortField } = useAppSelector((state: RootState) => state.device);

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestDeviceInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllDevices({
          ...data,
        }),
      );
    }
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
    });
  };

  /**
   * Handle the modal close function
   */
  const handleCloseModal = () => {
    fetchData({
      page,
      limit,
    });
    setModalBoxStatus('close');
  };

  /**
   * Handles the deletion of a device through the API
   *
   *
   */
  const handleDeleteDevice = (id: string) => {
    deviceApi
      .deleteDevice(id)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title="Device deleted Successfully "
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleCloseModal}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handles the pagination
   *
   */
  const handlePageChange = (changePage: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: changePage,
      limit,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setDeviceFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Displays a warning modal before deleting a device
   * @param {AvailableDevice} device - The device to be deleted
   */
  const handleDeleteDeviceWarning = (device: AvailableDevice) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: null,
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={device._id}
          title="Are you sure want to delete this Device?"
          description="Deleting your Device is permanent and cannot be undone. All the data, settings, and history will be lost. Are you sure you want to proceed?"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleDeleteDevice(id)}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Device edit handle function
   * @param {AvailableDevice} device - The device to be deleted
   */
  const editDeviceFunction = (device: AvailableDevice) => {
    navigate(`./edit/${device.id}`);
  };

  /**
   * Updates the availability status of a device in the local state
   * @param {string} deviceId - The ID of the device to update
   * @param {boolean} isAvailable - The new availability status
   */
  const updateAvailability = (deviceId: string, isAvailable: boolean) => {
    deviceApi
      .editAvailableStatusDevice(deviceId, { availabilityStatus: isAvailable })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title="Status Changed successfully..."
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({
                  page,
                  limit, // Need to fetch the current page
                });
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {AvailableDevice} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: AvailableDevice, checked: boolean) => {
    if (checked) {
      updateAvailability(device._id, checked);
    } else {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        header: false,
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message="You are disabling one of your device. This action will make the device unavailable to users,
            impacting service and revenue. If this is unintentional,
            please re-enable the charge point through your dashboard immediately."
            onConfirm={() => updateAvailability(device._id, checked)}
          />
        ),
        notificationModal: true,
      });
    }
  };

  /**
   * Renders the custom data for the serial number column
   * @param {AvailableDevice} device - The device data
   * @param {number} index - The current row index
   * @returns {JSX.Element} The rendered serial number cell
   */
  const renderSerialNumber = (device: AvailableDevice, index: number) => (
    <div className="table-row-each-data">{index + 1}</div>
  );

  /**
   * Renders the custom data for the availability column
   * @param {AvailableDevice} device - The device data
   * @returns {JSX.Element} The rendered availability toggle cell
   */
  const renderAvailability = (device: AvailableDevice) => (
    <div className="flex justify-center">
      <div key={device.chargingStation}>
        <SwitchButton
          checked={device.isAvailable}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  /**
   * UI of Status
   * @param device
   * @returns
   */
  const renderStatus = (device: AvailableDevice) =>
    device.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DodIcon />
          <span style={{ fontWeight: '700' }}>{device.isDeviceOffline ? 'Offline' : 'Online'}</span>
        </div>
      </div>
    ) : (
      <div />
    );
  /**
   * UI of Alerts
   * @param device
   * @returns
   */
  // const renderAlerts = (device: AvailableDevice) => (
  //   <div>
  //     <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px' }}>
  //       <AlertIcon />
  //       <span style={{ fontWeight: '700' }}>{device.alert}</span>
  //     </div>
  //   </div>
  // );

  /**
   * Function to handle qr code download
   */
  const handleDownloadQRCode = (device: AvailableDevice) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeviceQrCode
          handleClose={() => setModalBoxStatus('close')}
          deviceSN={device.chargingPointSn}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Function to reset the device
   * @param chargingPointSn
   */
  const resetDevice = async (chargingPointSn: string) => {
    await deviceApi
      .resetDevice({ chargingPointSn })
      .then(() => {
        setModalBoxDetail({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title="Device reset successfully..."
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({
                  page,
                  limit, // Need to fetch the current page
                });
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to reset device
   */
  const handleResetDevice = (device: AvailableDevice) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DisableSwitchWarning
          selectedId={device?.chargingPointSn}
          onCancel={() => setModalBoxStatus('close')}
          message={`Are you sure you want to reset a device(${device?.chargingPointName})`}
          onConfirm={() => resetDevice(device?.chargingPointSn)}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Renders the custom data for the actions column
   * @param {AvailableDevice} device - The device data
   * @returns {JSX.Element} The rendered actions cell
   */
  const renderActions = (device: AvailableDevice) => (
    <div className="action-button-group">
      <div
        className="action-button-item view-color"
        onClick={() => handleResetDevice(device)}
        role="presentation">
        <ResetIcon />
      </div>
      <div
        className="action-button-item view-color"
        onClick={() => handleDownloadQRCode(device)}
        role="presentation">
        <QRIcon />
      </div>
      <div
        className="action-button-item view-color"
        onClick={() => editDeviceFunction(device)}
        role="presentation">
        <ActionEditIcon />
      </div>
      <div
        role="button"
        tabIndex={0}
        aria-label="Delete device"
        className="action-button-item"
        onClick={() => handleDeleteDeviceWarning(device)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleDeleteDeviceWarning(device);
          }
        }}>
        {theme === 'light' ? <ActionDeleteIcon /> : <ActionDeleteIconDark />}
      </div>
    </div>
  );

  const column = [
    {
      label: 'S. No',
      accessorKey: 'sno' as keyof AvailableDevice,
      customData: renderSerialNumber,
      columnWidth: 45,
    },
    // {
    //   label: 'Device Name',
    //   accessorKey: 'name' as keyof AvailableDevice,
    // },
    {
      label: 'Station Name',
      accessorKey: 'chargingStation' as keyof AvailableDevice,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'Device Name',
      accessorKey: 'chargingPointName' as keyof AvailableDevice,
      queryParamsKey: 'deviceId',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
      sortAllow: true,
    },
    {
      label: 'Device Model',
      accessorKey: 'chargePointModel' as keyof AvailableDevice,
    },
    {
      label: 'Device Id',
      accessorKey: 'chargingPointSn' as keyof AvailableDevice,
    },
    {
      label: 'Max Power Output',
      accessorKey: 'maximumOutputPower' as keyof AvailableDevice,
      sortAllow: true,
      /**
       * Data UI in table
       * @param device
       * @returns
       */
      customData: (device: AvailableDevice) => (
        <div>
          {device?.maximumOutputPower && (
            <span>{`${device?.maximumOutputPower} ${device?.maximumOutputPowerUnit || 'kWh'}`}</span>
          )}
        </div>
      ),
    },
    {
      label: 'Desired Power Output',
      accessorKey: 'desiredPowerOutput' as keyof AvailableDevice,
      sortAllow: true,
      /**
       * Data UI in table
       * @param device
       * @returns
       */
      customData: (device: AvailableDevice) => (
        <div>
          {device?.chargeRate && (
            <span>
              {device?.chargeRate?.unit === 'W'
                ? `${device.chargeRate.value / 1000} KW`
                : `${device?.chargeRate?.value} Amps`}
            </span>
          )}
        </div>
      ),
    },
    // {
    //   label: 'LBC',
    //   accessorKey: 'lbc' as keyof AvailableDevice,
    // },
    {
      label: 'Connector 1',
      accessorKey: 'connector1' as keyof AvailableDevice,
      sortAllow: true,
    },
    {
      label: 'Connector 2',
      accessorKey: 'connector2' as keyof AvailableDevice,
      sortAllow: true,
    },
    {
      label: 'Availability',
      accessorKey: 'isAvailable' as keyof AvailableDevice,
      customData: renderAvailability,
    },
    {
      label: 'Device Status',
      accessorKey: 'isDeviceOffline' as keyof AvailableDevice,
      customData: renderStatus,
      sortAllow: false,
    },
    // {
    //   label: 'Alert',
    //   accessorKey: 'alert' as keyof AvailableDevice,
    //   customData: renderAlert,
    // sortAllow: false,
    // },
    {
      label: 'Actions',
      customData: renderActions,
      accessorKey: '' as keyof AvailableDevice,
      columnWidth: 150,
    },
  ];

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    deviceApi
      .fetchDevicesExport({ ...filters })
      .then((response) => {
        // Convert data to CSV format
        const headers = [
          'Station Name',
          'Device Name',
          'Device Model',
          'Device Id',
          'Max Power Output',
          'Connector 1',
          'Connector 2',
          'Availability',
          'Device Status',
        ];
        const rows = response.data.results.map((row: AvailableDevice) => [
          row.chargingStation || '',
          row.chargingPointName || '',
          row.chargePointModel || '',
          row.chargingPointSn || '',
          row.maximumOutputPower || '',
          row.connectorsStatus.connector1 || '',
          row.connectorsStatus.connector2 || '',
          row.isAvailable ? 'Available' : 'Unavailable',
          row.isDeviceOffline ? 'Offline' : 'Online',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'devices-list.csv');
          } else {
            exportPDF(headers, rows, 'devices-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  console.log(filters, 'filters____');

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="device-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="device-management-header">
        <PageTitle
          title="Device Management"
          buttonLabel="Add Device"
          buttonSvgIcon={<PlusIcon />}
          buttonType="submit"
          onClickButton={() => navigate('./add')}
        />
      </div>
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={devices}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          footerLeft={
            <div className="table-bottom-button-list">
              <div className="table-button-item">
                <ExportButton
                  label="Export Data"
                  buttonList={[
                    {
                      key: 'pdf',
                      allow: true,
                      /**
                       * Export pdf function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('pdf'),
                    },
                    {
                      key: 'csv',
                      allow: true,
                      /**
                       * Export csv function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('csv'),
                    },
                  ]}
                  disabled={devices.length === 0}
                  loader={exportLoader}
                />
              </div>
            </div>
          }
          topHeader={false} // Hide existing filters
          filters={filters}
          filtersToDispatch={updateDeviceFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
        />
      </div>
    </div>
  );
}

export default DeviceManagement;
